import React, { useCallback, useEffect, useMemo, useState } from 'react';

import CoordinationTable from './CoordinationTable';
import DayDetail from './DayDetail';
import MarkPicker from './MarkPicker';
import * as api from './api';
import { useToasts } from './Toasts';
import {
    MEMBER_STATE,
    MONTH_STATUS,
    QUICK_PERIOD,
    currentMonth,
    defaultMonth,
    isCoarsePointer,
    monthFromLocation,
    quickCategoryOf,
    shiftMonth,
    syncLocationMonth,
} from './constants';
import { LoadingMask } from './shared';

/**
 * 員工端「我的排班協調／劃假」。一般員工只需要這一頁。
 *
 * 心智模型：每天預設就是上班，只標記想休的日子。
 * 全體同仁的劃假都看得到（協調用），但只有自己那一列點得下去。
 *
 * ⚠️ 目標月份決定這一頁在哪一個階段（mode，後端 ScheduleCoordinationService::modeFor）：
 *   下月＝正式排班（休息日＋特休都可以）；下下月以後＝遠期預假（只能提休息日）。
 *   ⛔ 兩種階段不可以混稱，標題與提示一定要講清楚現在是哪一種。
 *   可選假別由後端的 category_options 決定，⛔ 前端不自己加減選項。
 *
 * 版面沿用 sched.css（.sc-card / .sc-stats / .sc-note / .sc-btn），
 * 這樣六個步驟看起來是同一套系統。⛔ 操作規則不整片塞在畫面上，只留一行短提示。
 */
const ScheduleCoordination = () => {
    const [toastLayer, notify] = useToasts();
    const [period, setPeriod] = useState(monthFromLocation);
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [picker, setPicker] = useState(null);
    // 當日明細（唯讀）：點日期欄頭時記下是哪一天，⛔ 不記任何可寫入的狀態
    const [dayDetail, setDayDetail] = useState(null);

    const load = useCallback(async (year, month, { silent } = {}) => {
        if (!silent) setLoading(true);
        try {
            const data = await api.fetchMonth(year, month, false);
            setPayload(data);
        } catch (err) {
            notify('error', '讀取排班資料失敗，請重新整理頁面');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        syncLocationMonth(period.year, period.month);
        load(period.year, period.month);
        // 換月份時把明細收掉，⛔ 不要讓上個月的那一天留在畫面上
        setDayDetail(null);
    }, [period, load]);

    const me = useMemo(
        () => (payload ? (payload.members || []).find((m) => m.is_self) : null),
        [payload]
    );

    // 左鍵快捷用的假別，由後端的 category_options 決定，⛔ 前端不自己加減選項
    const quickCategory = useMemo(
        () => quickCategoryOf(payload ? payload.category_options : null),
        [payload]
    );

    // 自己置頂，其他同仁照原順序在下面（驗收 1：自己的列醒目）
    const orderedPayload = useMemo(() => {
        if (!payload) return null;
        const members = payload.members || [];
        return {
            ...payload,
            members: [...members.filter((m) => m.is_self), ...members.filter((m) => !m.is_self)],
        };
    }, [payload]);

    const closePicker = useCallback(() => setPicker(null), []);
    const closeDayDetail = useCallback(() => setDayDetail(null), []);

    /**
     * 日期欄頭 → 當日明細（唯讀）。⛔ 這條路徑完全不寫入：
     * 它只看 payload 裡本來就有的全員 marks，⛔ 不打任何 API、⛔ 不碰任何格子。
     * 再點同一欄就收起來（第二次點＝關閉）。
     */
    const handleDayHeaderClick = useCallback((date) => {
        // 明細與劃假選單不同時存在：兩個面板疊在一起會互相蓋住
        setPicker(null);
        setDayDetail((current) => (current && current.date === date.date ? null : date));
    }, []);

    // 錨點存 selector 不存 element／rect：React 重繪後 popover 仍找得到同一格，
    // 捲動時也能拿到當下真實座標重新定位（見 MarkPicker 的定位規則）
    const openPicker = useCallback((member, date) => {
        setDayDetail(null);
        setPicker({
            target: { member, date, mark: null },
            anchorSelector: `[data-cell="${member.uuid}|${date.date}"]`,
        });
    }, []);

    const runWrite = useCallback(
        async (fn) => {
            setBusy(true);
            try {
                const res = await fn();
                closePicker();
                await load(period.year, period.month, { silent: true });
                notify('success', res.message || '已更新');
                // 人力提示（節點 A）：⚠️ 只提示，⛔ 不擋 —— 劃假在上一行已經存好了
                if (res.hint_message) {
                    notify('info', res.hint_message);
                }
            } catch (err) {
                notify('error', err.message);
                // 被鎖定／已發布這類狀態變化，重讀一次讓畫面跟後端一致
                await load(period.year, period.month, { silent: true });
            } finally {
                setBusy(false);
            }
        },
        [closePicker, load, notify, period]
    );

    /**
     * 左鍵快捷（桌機）＝ 直接畫「一般休・整天」，⛔ 不跳任何視窗。
     *   空白 → 一般休・整天；已劃別的假別／時段 → 覆寫成一般休・整天；
     *   已經就是一般休・整天 → 取消這天（左鍵唯一的清除方式）。
     * 觸控裝置沒有左右鍵之分，點一下一律開精簡選單 ——
     * ⛔ 不可因為桌機改成「左鍵＝一般休」就讓手機選不到特休與上／下半天。
     */
    const handleCellClick = useCallback(
        (member, date) => {
            if (!member.can_edit || busy) return;

            if (isCoarsePointer()) {
                openPicker(member, date);

                return;
            }

            if (!quickCategory) return;

            const mark = (member.marks || []).find((m) => m.date === date.date);
            const isQuick = !!mark && mark.category === quickCategory.value && mark.period === QUICK_PERIOD;

            runWrite(() =>
                isQuick ? api.clearMark(date.date) : api.setMark(date.date, quickCategory.value, QUICK_PERIOD)
            );
        },
        [busy, openPicker, quickCategory, runWrite]
    );

    /**
     * 右鍵＝開精簡選單（假別 × 時段），⛔ 先擋掉瀏覽器自己的右鍵選單。
     * 同事那一列一樣擋掉原生選單，但只提示唯讀，⛔ 不開選單、⛔ 不動任何資料。
     */
    const handleCellContextMenu = useCallback(
        (member, date, event) => {
            event.preventDefault();

            if (!member.can_edit) {
                notify('info', '同事的列是唯讀的：左鍵、右鍵都不會改到同事的資料。');

                return;
            }

            openPicker(member, date);
        },
        [notify, openPicker]
    );

    const handlePick = useCallback(
        (category, periodValue) =>
            runWrite(() => api.setMark(picker.target.date.date, category, periodValue)),
        [picker, runWrite]
    );

    const handleClear = useCallback(
        () => runWrite(() => api.clearMark(picker.target.date.date)),
        [picker, runWrite]
    );

    const handleDone = useCallback(
        (done) => {
            if (!done && !window.confirm('要取消「我劃完了」嗎？取消後店長會看到你還在填寫中。')) {
                return;
            }
            runWrite(() => api.setDone(period.year, period.month, done));
        },
        [period, runWrite]
    );

    if (loading && !payload) {
        return (
            <div className="sc-card">
                {toastLayer}
                <div className="sc-empty">讀取中…</div>
            </div>
        );
    }

    if (!payload) {
        return (
            <div className="sc-card sc-card--accent-red">
                {toastLayer}
                <div className="sc-empty">目前無法取得排班資料，請重新整理頁面。</div>
            </div>
        );
    }

    const editable = !!me && !!me.can_edit;
    const coarse = isCoarsePointer();
    // 操作提示跟著實際能用的手勢走：桌機講左鍵／右鍵，觸控裝置只有「點一下開選單」
    const quickLabel = quickCategory ? quickCategory.label : '一般休';
    const opsHint =
        (coarse
            ? '點自己那一列的日期 → 選假別與時段'
            : `左鍵：直接畫${quickLabel}・整天（再按一次取消）　｜　右鍵：選假別與時段`) +
        '　｜　點日期欄頭：看那天誰想休（只看不改）';

    // 當日明細的內容：直接從月表同一份 payload 濾出那天有劃假的人（自己排最前面），
    // ⛔ 不另外打 API、⛔ 不做任何判定 —— 看到的一定跟班表那一欄的格子一致。
    const dayDetailRows = dayDetail
        ? ((orderedPayload || {}).members || [])
              .map((m) => ({
                  uuid: m.uuid,
                  name: m.name,
                  is_self: !!m.is_self,
                  mark: (m.marks || []).find((mk) => mk.date === dayDetail.date) || null,
              }))
              .filter((row) => !!row.mark)
        : [];
    // 「我的順位」：後端算好的同一份（依職能拆軌、逐月輪替），⛔ 前端不自己算順位。
    // 沒設定任何順位軌道時整個 rank 是 null ⇒ 明細就不出現這一行。
    const myRank = (me && me.rank) || null;
    // popover 顯示的「目前：…」要跟著最新資料走，不能停在點擊當下的快照
    const pickerTarget = picker
        ? {
            ...picker.target,
            mark: (((me || {}).marks) || []).find((m) => m.date === picker.target.date.date) || null,
        }
        : null;
    const summary = (me && me.summary) || {};
    const allowance = payload.allowance || {};
    const isChangedAfterDone = !!me && me.state === MEMBER_STATE.CHANGED;
    const readOnly = payload.status === MONTH_STATUS.PUBLISHED || payload.status === MONTH_STATUS.LOCKED;
    // 遠期預假用橘色、下月正式排班用藍色 —— 沿用既有 sc-badge 色階，⛔ 不另做一套視覺
    const modeBadge =
        payload.mode === 'advance'
            ? 'sc-badge--orange'
            : payload.mode === 'next'
            ? 'sc-badge--blue'
            : 'sc-badge--grey';
    const statusBadge =
        payload.status === MONTH_STATUS.PUBLISHED
            ? 'sc-badge--green'
            : payload.status === MONTH_STATUS.LOCKED
            ? 'sc-badge--orange'
            : 'sc-badge--blue';
    const left =
        allowance.days === null || allowance.days === undefined
            ? null
            : Math.round((allowance.days - (summary.total_days || 0)) * 10) / 10;

    return (
        <div>
            {toastLayer}

            <header className="sched-head">
                <div className="sched-head__main">
                    <h1 className="sched-head__title">
                        我的排班協調／劃假
                        <span className="sched-head__period">
                            {payload.year} 年 {payload.month} 月
                        </span>
                        <span className={`sc-badge ${modeBadge}`} data-mode={payload.mode}>
                            {payload.mode_label}
                        </span>
                        <span className={`sc-badge ${statusBadge}`}>{payload.status_label}</span>
                        {readOnly && <span className="sc-badge sc-badge--grey">唯讀</span>}
                    </h1>
                    <p className="sched-head__lede">
                        每天預設就是上班，只要在自己那一列想休的日期上{coarse ? '點一下' : '按左鍵'}就畫好。
                        看得到其他同事已劃的休假（協調用），但只能改自己的。
                    </p>
                </div>
                <div className="sched-head__actions">
                    <button
                        type="button"
                        className="sc-btn"
                        onClick={() => setPeriod(shiftMonth(payload.year, payload.month, -1))}
                    >
                        ← 上個月
                    </button>
                    <button
                        type="button"
                        className="sc-btn"
                        title="回到預設要劃的月份（下個月）"
                        onClick={() => setPeriod(defaultMonth())}
                    >
                        預設月份
                    </button>
                    <button type="button" className="sc-btn" onClick={() => setPeriod(currentMonth())}>
                        本月
                    </button>
                    <button
                        type="button"
                        className="sc-btn"
                        onClick={() => setPeriod(shiftMonth(payload.year, payload.month, 1))}
                    >
                        下個月 →
                    </button>
                </div>
            </header>

            <div className="sc-stats">
                <div className="sc-stats__item sc-stats__item--blue">
                    <span className="sc-stats__label">本月可休{allowance.is_manual ? '（店長已調整）' : ''}</span>
                    <span className="sc-stats__value">
                        {allowance.days === null || allowance.days === undefined ? '—' : allowance.days}
                        <small>天</small>
                    </span>
                    {/* 身分分開記、天數合併算：上面是合計，這裡拆給員工看多出來的是國定假日 */}
                    {allowance.holiday_days > 0 && (
                        <span className="sc-stats__note" data-allowance-breakdown>
                            一般休 {allowance.regular_days === null ? 0 : allowance.regular_days}・國定假日{' '}
                            {allowance.holiday_days}
                        </span>
                    )}
                </div>
                <div className="sc-stats__item sc-stats__item--muted">
                    <span className="sc-stats__label">我已劃</span>
                    <span className="sc-stats__value">
                        {summary.total_days || 0}
                        <small>天</small>
                    </span>
                    <span className="sc-stats__note">
                        特休 {summary.annual_days || 0}・一般休 {summary.general_rest_days || 0}・半天{' '}
                        {summary.half_day_count || 0} 次
                    </span>
                </div>
                <div className={`sc-stats__item ${left !== null && left < 0 ? 'sc-stats__item--orange' : 'sc-stats__item--muted'}`}>
                    <span className="sc-stats__label">還可以劃</span>
                    <span className="sc-stats__value">
                        {left === null ? '—' : left}
                        <small>天</small>
                    </span>
                </div>
                <div className="sc-stats__item sc-stats__item--muted">
                    <span className="sc-stats__label">劃假截止日</span>
                    <span className="sc-stats__value" style={{ fontSize: '15px' }}>
                        {allowance.due_date || '未設定'}
                    </span>
                </div>
                <div
                    className={`sc-stats__item ${
                        me && me.state === MEMBER_STATE.DONE
                            ? 'sc-stats__item--green'
                            : isChangedAfterDone
                            ? 'sc-stats__item--orange'
                            : 'sc-stats__item--muted'
                    }`}
                >
                    <span className="sc-stats__label">我的狀態</span>
                    <span className="sc-stats__value" style={{ fontSize: '15px' }}>
                        {me ? me.state_label : '—'}
                    </span>
                </div>
            </div>

            {payload.mode_hint && (
                <div className={`sc-note ${payload.mode === 'advance' ? 'sc-note--orange' : ''}`}>
                    <strong>{payload.mode_label}</strong>：{payload.mode_hint}
                </div>
            )}

            {payload.status === MONTH_STATUS.PUBLISHED && (
                <div className="sc-note sc-note--blue">
                    <strong>本月班表已發布</strong>
                    {payload.published_at
                        ? `（${payload.published_at}${payload.published_by ? ' 由 ' + payload.published_by : ''}）`
                        : ''}
                    ，劃假已成為正式班表，此頁改為唯讀。 臨時需要調整，請改走{' '}
                    <a href={payload.dayoff_create_url}>休假（請假）</a> 流程，或直接與店長協調。
                </div>
            )}

            {payload.status === MONTH_STATUS.LOCKED && (
                <div className="sc-note sc-note--orange">
                    <strong>本月已由店長鎖定</strong>
                    {payload.locked_at
                        ? `（${payload.locked_at}${payload.locked_by ? ' 由 ' + payload.locked_by : ''}）`
                        : ''}
                    ，正在定案中，暫時無法新增、修改或取消劃假。需要更動請與店長聯絡。
                </div>
            )}

            {isChangedAfterDone && (
                <div className="sc-note sc-note--orange">
                    <strong>完成後有異動</strong>：你在按過「我劃完了」之後又改了劃假
                    {me.changed_after_done_at ? `（最後一次 ${me.changed_after_done_at}）` : ''}
                    {me.last_change_summary ? `：${me.last_change_summary}` : ''}
                    。店長需要重新確認
                    {editable ? '，改好後請再按一次「我劃完了」。' : '；本月已停止編輯，請直接與店長聯絡。'}
                </div>
            )}

            <section className="sc-card">
                <div className="sc-card__head">
                    <h2 className="sc-card__title">{payload.year} 年 {payload.month} 月月表</h2>
                    <span className="sc-card__hint">
                        {editable ? opsHint : '本月無法編輯，僅供查看　｜　點日期欄頭：看那天誰想休'}
                    </span>
                </div>

                <CoordinationTable
                    payload={orderedPayload}
                    onCellClick={editable ? handleCellClick : null}
                    onCellContextMenu={editable ? handleCellContextMenu : null}
                    onDayHeaderClick={handleDayHeaderClick}
                    activeCellKey={picker ? `${picker.target.member.uuid}|${picker.target.date.date}` : null}
                    activeDate={dayDetail ? dayDetail.date : null}
                    rankScope="self"
                />

                <div className="sc-card__foot" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                    {(payload.category_options || []).map((c) => (
                        <span key={c.value}>
                            <i
                                className="sc-dot"
                                style={{
                                    display: 'inline-block',
                                    width: '9px',
                                    height: '9px',
                                    borderRadius: '2px',
                                    marginRight: '4px',
                                    verticalAlign: '-1px',
                                    backgroundColor: c.color,
                                }}
                            />
                            {c.label}（可選全天／上半天／下半天）
                        </span>
                    ))}
                    <span>
                        <i
                            style={{
                                display: 'inline-block',
                                width: '9px',
                                height: '9px',
                                borderRadius: '2px',
                                marginRight: '4px',
                                verticalAlign: '-1px',
                                backgroundColor: '#fff',
                                border: '1px solid #cfd6dd',
                            }}
                        />
                        沒有標記＝上班
                    </span>
                    <span>
                        <i
                            style={{
                                display: 'inline-block',
                                minWidth: '16px',
                                padding: '0 4px',
                                borderRadius: '7px',
                                marginRight: '4px',
                                verticalAlign: '-1px',
                                fontSize: '9px',
                                fontWeight: 'bold',
                                fontStyle: 'normal',
                                lineHeight: '14px',
                                color: '#1864ab',
                                backgroundColor: '#e7f1fb',
                            }}
                        >
                            雙
                        </i>
                        日期下方的藍色徽章＝那天雙診（雙上／雙下＝只有該時段雙診）；「?」＝開診資料尚未設定
                    </span>
                    <span style={{ marginLeft: 'auto' }}>日期欄底的橘線＝那天人力可能不足，⚠️ 只是提醒，你照樣劃得下去</span>
                </div>

                {editable && (
                    <div className="sc-card__foot" style={{ background: '#fff', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        {me.state === MEMBER_STATE.DONE ? (
                            <React.Fragment>
                                <span className="sc-badge sc-badge--green">
                                    ✓ 已完成{me.done_at ? `（${me.done_at}）` : ''}
                                </span>
                                <button type="button" className="sc-btn" disabled={busy} onClick={() => handleDone(false)}>
                                    取消完成
                                </button>
                                <span className="sc-help" style={{ margin: 0 }}>
                                    店長發布前你仍然可以直接修改，改了會標示成「完成後有異動」。
                                </span>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <button
                                    type="button"
                                    className="sc-btn sc-btn--primary"
                                    data-action="mark-done"
                                    disabled={busy}
                                    onClick={() => handleDone(true)}
                                >
                                    我劃完了
                                </button>
                                <span className="sc-help" style={{ margin: 0 }}>
                                    {isChangedAfterDone
                                        ? '確認改好之後，再按一次讓店長知道可以重新確認。'
                                        : '整個月都劃好之後按這裡，店長就知道你完成了。'}
                                </span>
                            </React.Fragment>
                        )}
                    </div>
                )}
            </section>

            <details className="sc-more">
                <summary>一般休和特休有什麼不同？</summary>
                <div className="sc-more__body">
                    <ul>
                        <li>
                            <strong>一般休</strong>：班表發布後即為正式休假，⛔ <strong>不需要</strong>另外開請假單。
                        </li>
                        <li>
                            <strong>特休</strong>：班表發布後，「我的班表」與出勤對照會出現「一鍵帶入正式特休申請」，
                            按下去自動預填日期與全天／上半天／下半天，你確認送出即可 —— ⛔ 不用重打一次，⛔ 也不會自動核准。
                        </li>
                        <li>事假、病假、生理假等其他假別一律走正式請假單，⛔ 這一頁不處理。</li>
                    </ul>
                </div>
            </details>

            {dayDetail && (
                <DayDetail
                    anchorSelector={`[data-day-head="${dayDetail.date}"]`}
                    date={dayDetail}
                    rows={dayDetailRows}
                    myRank={payload.has_rank_tracks ? myRank : null}
                    onClose={closeDayDetail}
                />
            )}

            {picker && (
                <MarkPicker
                    anchorSelector={picker.anchorSelector}
                    target={pickerTarget}
                    categories={payload.category_options || []}
                    periods={payload.period_options || []}
                    busy={busy}
                    onPick={handlePick}
                    onClear={handleClear}
                    onClose={closePicker}
                />
            )}

            {busy && <LoadingMask>處理中...</LoadingMask>}
        </div>
    );
};

export default ScheduleCoordination;
